import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    table: {
        width: "100%",
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#e9ecef',
        paddingVertical: 3,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#f1f3f5',
    },
    tableHeaderCell: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        textAlign: 'center',
    },
    tableCell: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#212529',
        textAlign: 'center',
        paddingHorizontal: 1,
    },
    technicianCell: {
        flex: 2,
    },
    truckCell: {
        flex: 1,
    },
    timeCell: {
        flex: 1.5,
    },
    laborCell: {
        flex: 1,
    },
    travelCell: {
        flex: 1,
    },
    ncCell: {
        flex: 1,
    },
    notesCell: {
        flex: 2,
    },
    noDataText: {
        fontSize: 10,
        fontStyle: 'italic',
        color: '#6c757d',
        textAlign: 'center',
        marginTop: 10,
    },
});

interface ServiceTechnicianSectionProps {
    techs: Array<{
        technician: string;
        truck: string;
        timeStart: string;
        timeEnd: string;
        labor: number | string;
        travel: number | string;
        nc: number | string;
        notes: string;
    }>;
}

const ServiceTechnicianSection: React.FC<ServiceTechnicianSectionProps> = ({ 
    techs, 
}) => {
    const formatTime = (timeString: string) => {
        if (!timeString) return '';
        try {
            const date = new Date(timeString);
            return date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
            });
        } catch {
            return timeString;
        }
    };

    const formatHours = (hours: number | string) => {
        if (!hours || hours === '') return '';
        const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
        return isNaN(numHours) ? '' : numHours.toFixed(2);
    };

    // Filter out empty technician entries
    const validTechs = techs.filter(tech => tech.technician && tech.technician.trim() !== '');

    if (validTechs.length === 0) {
        return (
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Technician Details</Text>
                <Text style={styles.noDataText}>No technicians assigned</Text>
            </View>
        );
    }

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>Technician Details</Text>
            <View style={styles.table}>
                <View style={styles.tableHeader}>
                    <Text style={[styles.tableHeaderCell, styles.technicianCell]}>Technician</Text>
                    <Text style={[styles.tableHeaderCell, styles.truckCell]}>Truck</Text>                   
                    <Text style={[styles.tableHeaderCell, styles.laborCell]}>Labor (hrs)</Text>
                    <Text style={[styles.tableHeaderCell, styles.travelCell]}>Travel (hrs)</Text>                  
                </View>
                {validTechs.map((tech, index) => (
                    <View style={styles.tableRow} key={index}>
                        <Text style={[styles.tableCell, styles.technicianCell]}>
                            {tech.technician || ''}
                        </Text>
                        <Text style={[styles.tableCell, styles.truckCell]}>
                            {tech.truck || ''}
                        </Text>                        
                        <Text style={[styles.tableCell, styles.laborCell]}>
                            {formatHours(tech.labor)}
                        </Text>
                        <Text style={[styles.tableCell, styles.travelCell]}>
                            {formatHours(tech.travel)}
                        </Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

export default ServiceTechnicianSection;
